<template>
  <PanelCard v-if="rolesVisible" :title="I18n.t('user.panel.roles_title')">
    <div class="tw-flex tw-flex-col tw-gap-2">
      <SfRadioGroup
        :model-value="selectedRole"
        :options="roleOptions"
        data-testid="user-panel-roles"
        @update:model-value="selectRole"
      />

      <div
        v-if="companyCreationVisible"
        class="tw-flex tw-flex-col tw-gap-2 tw-pl-6"
      >
        <span class="tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
          {{ I18n.t('user.panel.additional_permissions') }}
        </span>
        <SfCheckbox
          v-model="companyCreationAccess"
          :label="I18n.t('user.accesses.company_creation_access')"
          data-testid="user-panel-company-creation-access"
        />
      </div>
    </div>
  </PanelCard>
</template>

<script setup lang="ts">
import { SfCheckbox, SfRadioGroup } from '@silverfin/ui-components'
import type { SfRadioOption, SfRadioValue } from '@silverfin/ui-components'

import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { ROLE_OPTION_KEYS } from '@app/application/user-management/constants'
import { userRoles } from '@app/application/user-management/types'
import { useUserPanelStore } from '@app/application/user-management/stores/user-panel'
import useI18n from '@app/shared/vue/composables/use-I18n'
import PanelCard from '@app/application/user-management/components/panel-card.vue'

defineOptions({ name: 'RolesSection' })

const I18n = useI18n()
const store = useUserPanelStore()
const { capabilities, companyCreationVisible, draft, rolesVisible } =
  storeToRefs(store)

const companyCreationAccess = store.draftField('companyCreationAccess')

const selectedRole = computed(() =>
  draft.value?.admin ? userRoles.ADMIN : userRoles.STAFF
)

// The last admin of the firm cannot step down, so only Staff is closed off and
// the reason stays on screen for every user, keyboard ones included.
const roleOptions = computed<SfRadioOption[]>(() =>
  ROLE_OPTION_KEYS.map(({ value, label, description }) => {
    const blocked =
      value === userRoles.STAFF && capabilities.value.lastAdminInFirm

    return {
      value,
      label: I18n.t(label),
      description: I18n.t(description),
      disabled: blocked,
      note: blocked ? I18n.t('user.form.last_admin_user_hint') : undefined,
    }
  })
)

function selectRole(value: SfRadioValue): void {
  if (!draft.value) return

  draft.value.admin = value === userRoles.ADMIN

  // An admin gets file creation anyway and the checkbox disappears, so the
  // permission must not stay in the draft where the save would still send it.
  if (draft.value.admin) draft.value.companyCreationAccess = false
}
</script>
